import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import CmsPage from "@/pages/CmsPage";
import Help from "@/pages/Help";
import Demos from "@/pages/Demos";
import HowItWorks from "@/pages/HowItWorks";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refund from "@/pages/Refund";
import BillingReturn from "@/pages/BillingReturn";
import Signup from "@/pages/Signup";
import VerifyOtp from "@/pages/VerifyOtp";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import DoctorDashboard from "@/pages/DoctorDashboard";
import DoctorPackages from "@/pages/DoctorPackages";
import DoctorCheckout from "@/pages/DoctorCheckout";
import DoctorInvoices from "@/pages/DoctorInvoices";
import DoctorFeatures from "@/pages/DoctorFeatures";
import OnboardingWizard from "@/pages/OnboardingWizard";
import DoctorFinalReview from "@/pages/DoctorFinalReview";
import DoctorPublish from "@/pages/DoctorPublish";
import LockedFeature from "@/pages/LockedFeature";
import ComingSoon from "@/pages/ComingSoon";
import AIStudio from "@/pages/AIStudio";
import Analytics from "@/pages/Analytics";
import Documents from "@/pages/Documents";
import Leads from "@/pages/Leads";
import DoctorAppointments from "@/pages/DoctorAppointments";
import DoctorSecondOpinions from "@/pages/DoctorSecondOpinions";
import DoctorContentTrust from "@/pages/DoctorContentTrust";
import DoctorSmartFAQ from "@/pages/DoctorSmartFAQ";
import PublicCheckout from "@/pages/PublicCheckout";
import Billing from "@/pages/Billing";
import Domain from "@/pages/Domain";
import Settings from "@/pages/Settings";
import Gallery from "@/pages/Gallery";
import QRCard from "@/pages/QRCard";
import SEO from "@/pages/SEO";
import Support from "@/pages/Support";
import Articles from "@/pages/Articles";
import Credentials from "@/pages/Credentials";
import PublicDoctorRedirect from "@/pages/PublicDoctorRedirect";
import PublicCardRedirect from "@/pages/PublicCardRedirect";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminQueue from "@/pages/AdminQueue";
import AdminReview from "@/pages/AdminReview";
import AdminDoctorReviews from "@/pages/AdminDoctorReviews";
import AdminDoctors from "@/pages/AdminDoctors";
import AdminSupport from "@/pages/AdminSupport";
import AdminOperations from "@/pages/AdminOperations";
import AdminPayments from "@/pages/AdminPayments";
import AdminAIJobs from "@/pages/AdminAIJobs";
import AdminPublishing from "@/pages/AdminPublishing";
import AdminLeads from "@/pages/AdminLeads";
import AdminActivity from "@/pages/AdminActivity";
import {
    SuperAdminOverview,
    SuperAdminPackages,
    SuperAdminFeatures,
    SuperAdminTemplates,
    SuperAdminAdmins,
    SuperAdminAudit,
    SuperAdminSettings,
} from "@/pages/SuperAdmin";
import AdminLogin from "@/pages/AdminLogin";
import SuperAdminLogin from "@/pages/SuperAdminLogin";
import PortalGate from "@/components/PortalGate";
import SuperAdminIntegrations from "@/pages/SuperAdminIntegrations";
import SuperAdminSearchRankings from "@/pages/SuperAdminSearchRankings";
import SuperAdminMaintenance from "@/pages/SuperAdminMaintenance";
import SuperAdminMaintenanceMode from "@/pages/SuperAdminMaintenanceMode";
import MaintenancePage from "@/pages/MaintenancePage";
import useMaintenanceStatus from "@/hooks/useMaintenanceStatus";
import SuperAdminSiteContent from "@/pages/SuperAdminSiteContent";
import SuperAdminMarketing from "@/pages/SuperAdminMarketing";
import SuperAdminMarketingPages from "@/pages/SuperAdminMarketingPages";
import SuperAdminWebsiteAnalytics from "@/pages/SuperAdminWebsiteAnalytics";
import SuperAdminBusiness from "@/pages/SuperAdminBusiness";
import SuperAdminCommunications from "@/pages/SuperAdminCommunications";
import SuperAdminCRM from "@/pages/SuperAdminCRM";
import SuperAdminDashboards from "@/pages/SuperAdminDashboards";
import SuperAdminAutomation from "@/pages/SuperAdminAutomation";
import DoctorFeedback from "@/pages/DoctorFeedback";
import DoctorFaqs from "@/pages/DoctorFaqs";
import IdentityScore from "@/pages/IdentityScore";
import DoctorSocial from "@/pages/DoctorSocial";
import DoctorsDirectory from "@/pages/DoctorsDirectory";
import AuthGoogleCallback from "@/pages/AuthGoogleCallback";
import CompleteProfile from "@/pages/CompleteProfile";
import DoctorContentHub from "@/pages/DoctorContentHub";
import DoctorProfileExt from "@/pages/DoctorProfileExt";
import DoctorAILanguage from "@/pages/DoctorAILanguage";
import DoctorTranslations from "@/pages/DoctorTranslations";
import { getToken, getUser } from "@/lib/api";

function RequireAuth({ role, children }) {
    const token = getToken();
    const user = getUser();
    const loc = useLocation();
    // Choose the correct sign-in doorway based on where the visitor was headed:
    //   • /super-admin/* → /super-admin/login
    //   • /admin/*       → /admin/login
    //   • anything else  → /login (generic / doctor)
    // Preserve the intended destination so post-login the user lands where they
    // were originally headed (?next=<pathname>).
    const pickLoginRoute = () => {
        if (loc.pathname.startsWith("/super-admin")) return "/super-admin/login";
        if (loc.pathname.startsWith("/admin")) return "/admin/login";
        return "/login";
    };
    if (!token || !user) {
        const next = loc.pathname + loc.search;
        const to = `${pickLoginRoute()}?next=${encodeURIComponent(next)}`;
        return <Navigate to={to} replace />;
    }
    if (role) {
        const roles = Array.isArray(role) ? role : [role];
        if (!roles.includes(user.role)) {
            if (user.role === "doctor") return <Navigate to="/doctor" replace />;
            if (user.role === "super_admin") return <Navigate to="/super-admin" replace />;
            return <Navigate to="/admin" replace />;
        }
    }
    return children;
}

import PageMeta from "@/components/PageMeta";

function RouteMeta() {
    // Watches URL changes and injects <title>/<meta>/canonical/robots for
    // every SPA route. See components/PageMeta.js for the metadata map.
    const loc = useLocation();
    return <PageMeta path={loc.pathname + loc.search} />;
}

function MaintenanceGate({ children }) {
    // Global gate — renders the branded "We'll be back" page when maintenance
    // is enabled AND the visitor is NOT a super admin AND the current path is
    // NOT part of the super-admin login/portal (so it stays turn-off-able).
    const status = useMaintenanceStatus();
    const loc = useLocation();
    const user = getUser();
    const isSuperAdmin = user?.role === "super_admin";
    const path = loc.pathname;
    const isSuperAdminSurface =
        path.startsWith("/super-admin") ||
        path === "/admin/login" ||
        path === "/super-admin/login";
    // Admin logins must be reachable so a super admin can log in and turn
    // maintenance off; the /super-admin/* protected routes are gated by
    // RequireAuth so a non-super-admin can never bypass by URL guessing.
    const isDoctorPublicSurface = path.startsWith("/dr/") || path.startsWith("/card/");
    if (status?.enabled && !isSuperAdmin && !isSuperAdminSurface) {
        if (isDoctorPublicSurface && !status.include_doctor_public_pages) {
            return children; // published /dr/ pages stay LIVE by default
        }
        return <MaintenancePage status={status} />;
    }
    return children;
}

function App() {
    return (
        <div className="App bg-[#F5F7FB]">
            <BrowserRouter>
                <RouteMeta />
                <MaintenanceGate>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/doctors" element={<DoctorsDirectory />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/refund" element={<Refund />} />
                    <Route path="/p/:slug" element={<CmsPage />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/demos" element={<Demos />} />
                    <Route path="/products" element={<CmsPage slugOverride="products-and-services" />} />
                    <Route path="/features" element={<CmsPage slugOverride="features" />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/billing/return" element={<BillingReturn />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/verify-otp" element={<VerifyOtp />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/admin/login" element={
                        <PortalGate><AdminLogin /></PortalGate>
                    } />
                    <Route path="/super-admin/login" element={
                        <PortalGate><SuperAdminLogin /></PortalGate>
                    } />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/auth/google/callback" element={<AuthGoogleCallback />} />
                    <Route path="/signup/complete-profile" element={<RequireAuth role="doctor"><CompleteProfile /></RequireAuth>} />
                    <Route path="/doctor/content" element={<RequireAuth role="doctor"><DoctorContentHub /></RequireAuth>} />
                    <Route path="/doctor/identity" element={<RequireAuth role="doctor"><DoctorProfileExt /></RequireAuth>} />
                    <Route path="/doctor/ai-language" element={<RequireAuth role="doctor"><DoctorAILanguage /></RequireAuth>} />
                    <Route path="/doctor/translations" element={<RequireAuth role="doctor"><DoctorTranslations /></RequireAuth>} />

                    <Route path="/doctor" element={<RequireAuth role="doctor"><DoctorDashboard /></RequireAuth>} />
                    <Route path="/doctor/packages" element={<RequireAuth role="doctor"><DoctorPackages /></RequireAuth>} />
                    <Route path="/doctor/checkout/:orderId" element={<RequireAuth role="doctor"><DoctorCheckout /></RequireAuth>} />
                    <Route path="/doctor/invoices" element={<RequireAuth role="doctor"><DoctorInvoices /></RequireAuth>} />
                    <Route path="/doctor/features" element={<RequireAuth role="doctor"><DoctorFeatures /></RequireAuth>} />
                    <Route path="/doctor/onboarding" element={<RequireAuth role="doctor"><OnboardingWizard /></RequireAuth>} />
                    <Route path="/doctor/credentials" element={<RequireAuth role="doctor"><Credentials /></RequireAuth>} />
                    <Route path="/doctor/review" element={<RequireAuth role="doctor"><DoctorFinalReview /></RequireAuth>} />
                    <Route path="/doctor/publish" element={<RequireAuth role="doctor"><DoctorPublish /></RequireAuth>} />
                    <Route path="/doctor/ai-studio" element={<RequireAuth role="doctor"><AIStudio /></RequireAuth>} />
                    <Route path="/doctor/analytics" element={<RequireAuth role="doctor"><Analytics /></RequireAuth>} />
                    <Route path="/doctor/documents" element={<RequireAuth role="doctor"><Documents /></RequireAuth>} />
                    <Route path="/doctor/leads" element={<RequireAuth role="doctor"><Leads /></RequireAuth>} />
                    <Route path="/doctor/appointments" element={<RequireAuth role="doctor"><DoctorAppointments /></RequireAuth>} />
                    <Route path="/doctor/second-opinions" element={<RequireAuth role="doctor"><DoctorSecondOpinions /></RequireAuth>} />
                    <Route path="/doctor/content-trust" element={<RequireAuth role="doctor"><DoctorContentTrust /></RequireAuth>} />
                    <Route path="/doctor/smart-faq" element={<RequireAuth role="doctor"><DoctorSmartFAQ /></RequireAuth>} />
                    {/* iter90-fix · Public patient-facing checkout for Second Opinion */}
                    <Route path="/checkout/mock/:orderId" element={<PublicCheckout />} />
                    <Route path="/doctor/billing" element={<RequireAuth role="doctor"><Billing /></RequireAuth>} />
                    <Route path="/doctor/domain" element={<RequireAuth role="doctor"><Domain /></RequireAuth>} />
                    <Route path="/doctor/settings" element={<RequireAuth role="doctor"><Settings /></RequireAuth>} />
                    <Route path="/doctor/gallery" element={<RequireAuth role="doctor"><Gallery /></RequireAuth>} />
                    <Route path="/doctor/qr-card" element={<RequireAuth role="doctor"><QRCard /></RequireAuth>} />
                    <Route path="/doctor/seo" element={<RequireAuth role="doctor"><SEO /></RequireAuth>} />
                    <Route path="/doctor/support" element={<RequireAuth role="doctor"><Support /></RequireAuth>} />
                    <Route path="/doctor/articles" element={<RequireAuth role="doctor"><Articles /></RequireAuth>} />
                    <Route path="/doctor/social" element={<RequireAuth role="doctor"><DoctorSocial /></RequireAuth>} />
                    <Route path="/doctor/locked" element={<RequireAuth role="doctor"><LockedFeature /></RequireAuth>} />
                    <Route path="/doctor/coming-soon" element={<RequireAuth role="doctor"><ComingSoon /></RequireAuth>} />
                    <Route path="/doctor/feature/:code" element={<RequireAuth role="doctor"><LockedFeature /></RequireAuth>} />

                    <Route path="/dr/:slug" element={<PublicDoctorRedirect />} />
                    <Route path="/dr/:slug/articles/:articleSlug" element={<PublicDoctorRedirect />} />
                    {/* iter66 B1 · Multi-page doctor site subpages */}
                    <Route path="/dr/:slug/:page" element={<PublicDoctorRedirect />} />
                    <Route path="/card/:slug" element={<PublicCardRedirect />} />

                    <Route path="/admin" element={<RequireAuth role={["admin", "super_admin"]}><AdminDashboard /></RequireAuth>} />
                    <Route path="/admin/operations" element={<RequireAuth role={["admin", "super_admin"]}><AdminOperations /></RequireAuth>} />
                    <Route path="/admin/queue" element={<RequireAuth role={["admin", "super_admin"]}><AdminQueue /></RequireAuth>} />
                    <Route path="/admin/doctors" element={<RequireAuth role={["admin", "super_admin"]}><AdminDoctors /></RequireAuth>} />
                    <Route path="/admin/review/:doctorId" element={<RequireAuth role={["admin", "super_admin"]}><AdminReview /></RequireAuth>} />
                    <Route path="/admin/doctors/:doctorId/reviews" element={<RequireAuth role={["admin", "super_admin"]}><AdminDoctorReviews /></RequireAuth>} />
                    <Route path="/admin/payments" element={<RequireAuth role={["admin", "super_admin"]}><AdminPayments /></RequireAuth>} />
                    <Route path="/admin/ai-jobs" element={<RequireAuth role={["admin", "super_admin"]}><AdminAIJobs /></RequireAuth>} />
                    <Route path="/admin/publishing" element={<RequireAuth role={["admin", "super_admin"]}><AdminPublishing /></RequireAuth>} />
                    <Route path="/admin/leads" element={<RequireAuth role={["admin", "super_admin"]}><AdminLeads /></RequireAuth>} />
                    <Route path="/admin/support" element={<RequireAuth role={["admin", "super_admin"]}><AdminSupport /></RequireAuth>} />
                    <Route path="/admin/activity" element={<RequireAuth role={["admin", "super_admin"]}><AdminActivity /></RequireAuth>} />

                    <Route path="/super-admin" element={<RequireAuth role="super_admin"><SuperAdminOverview /></RequireAuth>} />
                    <Route path="/super-admin/packages" element={<RequireAuth role="super_admin"><SuperAdminPackages /></RequireAuth>} />
                    <Route path="/super-admin/features" element={<RequireAuth role="super_admin"><SuperAdminFeatures /></RequireAuth>} />
                    <Route path="/super-admin/templates" element={<RequireAuth role="super_admin"><SuperAdminTemplates /></RequireAuth>} />
                    <Route path="/super-admin/admins" element={<RequireAuth role="super_admin"><SuperAdminAdmins /></RequireAuth>} />
                    <Route path="/super-admin/audit" element={<RequireAuth role="super_admin"><SuperAdminAudit /></RequireAuth>} />
                    <Route path="/super-admin/settings" element={<RequireAuth role="super_admin"><SuperAdminSettings /></RequireAuth>} />
                    <Route path="/super-admin/integrations" element={<RequireAuth role="super_admin"><SuperAdminIntegrations /></RequireAuth>} />
                    <Route path="/super-admin/search-rankings" element={<RequireAuth role="super_admin"><SuperAdminSearchRankings /></RequireAuth>} />
                    <Route path="/super-admin/maintenance" element={<RequireAuth role="super_admin"><SuperAdminMaintenance /></RequireAuth>} />
                    <Route path="/super-admin/maintenance-mode" element={<RequireAuth role="super_admin"><SuperAdminMaintenanceMode /></RequireAuth>} />
                    <Route path="/super-admin/site-content" element={<RequireAuth role="super_admin"><SuperAdminSiteContent /></RequireAuth>} />
                    <Route path="/super-admin/marketing" element={<RequireAuth role="super_admin"><SuperAdminMarketing /></RequireAuth>} />
                    <Route path="/super-admin/marketing-pages" element={<RequireAuth role="super_admin"><SuperAdminMarketingPages /></RequireAuth>} />
                    <Route path="/super-admin/analytics" element={<RequireAuth role="super_admin"><SuperAdminWebsiteAnalytics /></RequireAuth>} />
                    <Route path="/super-admin/business" element={<RequireAuth role="super_admin"><SuperAdminBusiness /></RequireAuth>} />
                    <Route path="/super-admin/communications" element={<RequireAuth role="super_admin"><SuperAdminCommunications /></RequireAuth>} />
                    <Route path="/super-admin/crm" element={<RequireAuth role="super_admin"><SuperAdminCRM /></RequireAuth>} />
                    <Route path="/admin/crm" element={<RequireAuth role={["admin", "super_admin"]}><SuperAdminCRM /></RequireAuth>} />
                    <Route path="/super-admin/dashboards" element={<RequireAuth role="super_admin"><SuperAdminDashboards /></RequireAuth>} />
                    <Route path="/super-admin/automation" element={<RequireAuth role="super_admin"><SuperAdminAutomation /></RequireAuth>} />
                    <Route path="/doctor/feedback" element={<RequireAuth role="doctor"><DoctorFeedback /></RequireAuth>} />
                    <Route path="/doctor/faqs" element={<RequireAuth role="doctor"><DoctorFaqs /></RequireAuth>} />
                    <Route path="/identity-score" element={<IdentityScore />} />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </MaintenanceGate>
            </BrowserRouter>
            <Toaster position="top-right" richColors closeButton toastOptions={{
                classNames: {
                    toast: "!rounded-xl !border !border-[#E5E9F2] !ddi-shadow-md !py-3 !px-4",
                    title: "!text-[13.5px] !font-semibold",
                    description: "!text-[12.5px] !text-[#64748B]",
                }
            }} />
        </div>
    );
}

export default App;
